import React, { useState } from 'react';
import { Box, Button, Typography, CircularProgress, SxProps, Theme, Tooltip } from '@mui/material';
import { useRazorpay, RazorpayOrderOptions } from 'react-razorpay';
import COLORS from '../Utils/colors';
import { request } from '../Utils/request';
import { rupeesToPaise } from '../Utils/currencyConverter';

export interface PaymentButtonProps {
  /** Amount to display on the button */
  amount: number;
  /** Currency code (default: INR) */
  currency?: 'INR' | 'USD' | 'EUR' | 'GBP';
  /** Disabled state */
  disabled?: boolean;
  /** Custom button text */
  buttonText?: string;
  /** Payment for description */
  paymentFor?: string;
  /** Reference ID for the payment */
  referenceId?: string;
  /** Called when payment is successful */
  onPaymentSuccess?: (razorpay_payment_id: string) => void;
  /** Called when payment fails */
  onPaymentError?: (error: any) => void;
  /** Custom styles for the button */
  sx?: SxProps<Theme>;
  /** Button variant */
  variant?: 'contained' | 'outlined' | 'text';
  /** Button size */
  size?: 'small' | 'medium' | 'large';
  /** User data for prefill */
  userData: {
    name: string;
    // email: string;
    contact: string;
  };
}

/**
 * A custom payment button component with amount display and loading states.
 * Designed to integrate with payment gateways like Razorpay.
 */
export const PaymentButton: React.FC<PaymentButtonProps> = ({
  amount,
  currency = 'INR',
  disabled = false,
  buttonText = 'Pay Now',
  paymentFor = 'payment',
  referenceId,
  onPaymentSuccess,
  onPaymentError,
  sx,
  variant = 'contained',
  size = 'large',
  userData,
}) => {
  // The hook loads Razorpay script and exposes states
  const { isLoading, Razorpay } = useRazorpay();
  const [isProcessing, setIsProcessing] = useState(false);
  const [orderId, setOrderId] = useState<string>('');
  const [razorpayOrderId, setRazorpayOrderId] = useState<string | undefined>('');

  const isRazorpayReady = typeof window !== 'undefined' && Boolean((window as any).Razorpay);
  const isScriptLoading = !isRazorpayReady && isLoading;

  const formatAmount = (amount: number, currency: 'INR' | 'USD' | 'EUR' | 'GBP') => {
    if (currency === 'INR') {
      return `₹${amount.toFixed(2)}`;
    }
    return `${currency} ${amount.toFixed(2)}`;
  };

  // Validation checks for disable conditions
  const isUserDataValid = userData && 
    userData.name?.trim() && 
    // userData.email?.trim() && 
    userData.contact?.trim();
  
  const isAmountValid = amount > 0;
  const isReferenceValid = referenceId && referenceId.trim();
  
  const isDisabled = disabled || 
    isScriptLoading || 
    isProcessing || 
    !isUserDataValid || 
    !isAmountValid || 
    !isReferenceValid;

  // Get the reason why the button is disabled
  const getDisableReason = () => {
    if (disabled) return 'Button is disabled';
    if (isScriptLoading) return 'Loading Razorpay...';
    if (isProcessing) return 'Processing payment...';
    if (!isUserDataValid) return 'Please fill in all user details';
    if (!isAmountValid) return 'Invalid amount';
    if (!isReferenceValid) return 'Reference ID is required';
    return '';
  };

  const onPaymentSuccessAPI = async (data:any) => { // this is the function that will be called when the payment is successful
    const res = await request<{ data:any, razorpay_payment_id: string }>('/payments/verify', {
      method: 'POST',
      body: data,
      authorization: true,
    });
    onPaymentSuccess?.(res?.data?.paymentId);
  }

  const handlePayment = async () => {
    if (!Razorpay || !isRazorpayReady) {
      console.error('Razorpay script is not ready yet.');
      setIsProcessing(false);
      onPaymentError?.(new Error('Razorpay is still loading. Please try again in a moment.'));
      return;
    }
    
    setIsProcessing(true);
    
    try {
              // Create order
        console.log("amount0", referenceId);
        let orderIdFromAPI: string = '';
        
        try {
          const res = await request<{ data:any, razorpayOrderId: string }>('/payments/create-order', {
            method: 'POST',
            body: {
              amount: rupeesToPaise(amount),
              currency: currency,
              paymentFor: paymentFor,
              referenceId: referenceId || '',
            },
            authorization: true,
          });

          console.log("order_id response", res);
          orderIdFromAPI = res?.data?.razorpayOrderId;
          setOrderId(orderIdFromAPI);
          setRazorpayOrderId(orderIdFromAPI);
        } catch (err) {
          console.error('Order creation failed', err);
          onPaymentError?.(err);
          setIsProcessing(false);
          return;
        }
        
        console.log("amount", amount);
        const options: RazorpayOrderOptions = {
          key: process.env.REACT_APP_RAZORPAY_KEY_ID || '',
          amount: rupeesToPaise(amount),
          currency: currency,
          name: 'iFind',
          description: `${paymentFor} payment`,
          order_id: orderIdFromAPI, // Use the API response directly
          handler: (resp: any) => {
              console.log("razorpay response", resp);
              const data = {
                  "razorpay_order_id": orderIdFromAPI, // Use the API response directly
                  "razorpay_payment_id": resp.razorpay_payment_id,
                  "razorpay_signature": resp.razorpay_signature
              } 
            console.log('Payment success', data,'---',{orderId: orderIdFromAPI, razorpayOrderId: orderIdFromAPI, resp});
            setIsProcessing(false);
            onPaymentSuccessAPI(data); // this is the function that will be called when the payment is successful
          },
        prefill: userData,
        theme: { color: COLORS.primary },
        modal: {
          ondismiss: () => {
            setIsProcessing(false);
          },
        },
      };

      const rzp = new Razorpay(options);
      rzp.open();
    } catch (err) {
      console.error('Payment failed', err);
      setIsProcessing(false);
      onPaymentError?.(err);
    }
  };

  return (
    <Box sx={{ width: '100%', ...sx }}>
      <Tooltip 
        title={isDisabled ? getDisableReason() : ''} 
        placement="top"
        arrow
      >
        <span style={{ width: '100%' }}>
          <Button
            variant={variant}
            size={size}
            onClick={handlePayment}
            disabled={isDisabled}
            sx={{
          width: '100%',
          height: 56,
          borderRadius: 2,
          textTransform: 'none',
          fontSize: '1.1rem',
          fontWeight: 600,
          boxShadow: variant === 'contained' ? '0 4px 12px rgba(255, 107, 53, 0.3)' : 'none',
          bgcolor: variant === 'contained' ? COLORS.primary : 'transparent',
          color: variant === 'contained' ? 'white' : COLORS.primary,
          border: variant === 'outlined' ? `2px solid ${COLORS.primary}` : 'none',
          '&:hover': {
            bgcolor: variant === 'contained' ? COLORS.burntOrange : 'rgba(255, 107, 53, 0.08)',
            boxShadow: variant === 'contained' ? '0 6px 16px rgba(255, 107, 53, 0.4)' : 'none',
          },
          '&:disabled': {
            bgcolor: variant === 'contained' ? COLORS.grey : 'transparent',
            color: variant === 'contained' ? 'white' : COLORS.grey,
            borderColor: COLORS.grey,
          },
          ...sx,
        }}
      >
        {isScriptLoading || isProcessing ? (
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <CircularProgress size={20} color="inherit" />
            <Typography variant="body1">
              {isScriptLoading ? 'Loading Razorpay...' : 'Processing...'}
            </Typography>
          </Box>
        ) : (
          <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
            <Typography variant="body2" sx={{ opacity: 0.8, mb: 0.5 }}>
              {buttonText}
            </Typography>
            <Typography variant="h6" sx={{ fontWeight: 700 }}>
              {formatAmount(amount, currency)}
            </Typography>
          </Box>
        )}
      </Button>
        </span>
      </Tooltip>
      
      {/* Helper text for validation errors */}
      {isDisabled && !isScriptLoading && !isProcessing && (
        <Typography 
          variant="caption" 
          color="error" 
          sx={{ mt: 1, textAlign: 'center', display: 'block' }}
        >
          {getDisableReason()}
        </Typography>
      )}
    </Box>
  );
};

export default PaymentButton;
